/**
 * Core types and interfaces for the modular visualizer system
 */

import { Wave } from '../../../types';
import { colorHelper } from './ColorModeHelper';

export type ViewMode = 
  | 'time' 
  | 'waveform' 
  | 'oscilloscope' 
  | 'oscilloscope-xy' 
  | 'oscilloscope-xy-webgl'
  | 'lissajous' 
  | 'xyz' 
  | 'chladni' 
  | 'fluid' 
  | 'water' 
  | 'galaxy' 
  | 'sacred' 
  | 'dmt' 
  | 'audiobloom';

export type ColorMode = 'wave' | 'channel';

export interface ViewState {
  zoom: number;
  pan: { x: number; y: number };
}

export interface VisualizerConfig {
  // Canvas context and dimensions
  ctx: CanvasRenderingContext2D | null;
  canvas?: HTMLCanvasElement;
  width: number;
  height: number;
  
  // Audio data - visualizers only receive normalized waves
  waves: Wave[];
  activeWaves: Wave[];
  speed: number;
  time: number;
  deltaTime: number;
  
  // Raw uploaded audio frequency data (for visualizers that want to parse it themselves)
  uploadedAudioData?: Float32Array | null;
  isUploadedAudioPlaying?: boolean;
  
  // Waveform data (for XY oscilloscope which needs raw samples)
  waveformData?: { left: Float32Array; right: Float32Array } | null;
  
  // View state
  zoom: number;
  pan: { x: number; y: number };
  
  // Auto rotation state (for 3D visualizers)
  isAutoRotating?: boolean;
  autoRotRef?: number;
  
  // Mouse interaction
  mousePos?: { x: number; y: number } | null;
  
  // Music analysis (smoothed data for reactive visuals like Galaxy, Sacred, DMT)
  smoothedMusicData?: {
    bassEnergy: number;
    midEnergy: number;
    trebleEnergy: number;
    totalEnergy: number;
  } | null;
  
  // Color mode (wave colors vs channel-based coloring)
  colorMode?: ColorMode;
  
  // Merge stereo channels into single output
  mergeChannels?: boolean;
  
  // Callbacks for state updates
  setHoveredFrequency?: (freq: { freq: number; amp: number; channel: string; color: string } | null) => void;
}

export interface VisualizerMetadata {
  id: ViewMode;
  name: string;
  description: string;
  category: 'time' | '2d' | '3d' | 'physics' | 'artistic';
  defaultZoom: number;
  supports3D: boolean;
  supportsAutoRotation: boolean;
  supportsPanning: boolean;
  supportsZooming: boolean;
  usesWebGL?: boolean; // If true, visualizer uses WebGL and doesn't need 2D context
}

/**
 * Base interface that all visualizers must implement
 */
export interface IVisualizer {
  /** Unique identifier for this visualizer */
  readonly metadata: VisualizerMetadata;
  
  /**
   * Render the visualizer
   * @param config - Configuration object containing all necessary data and state
   * @returns Updated auto rotation value if applicable, otherwise undefined
   */
  render(config: VisualizerConfig): number | void;
  
  /**
   * Optional: Called when the visualizer is activated
   */
  onActivate?(): void;
  
  /**
   * Optional: Called when the visualizer is deactivated
   */
  onDeactivate?(): void;
  
  /**
   * Optional: Handle custom wheel events (for visualizers that need special zoom behavior)
   * @returns true if event was handled, false to use default behavior
   */
  handleWheel?(event: WheelEvent, config: VisualizerConfig): boolean;
}

/**
 * Abstract base class for visualizers - provides common utilities
 */
export abstract class BaseVisualizer implements IVisualizer {
  abstract readonly metadata: VisualizerMetadata;
  
  abstract render(config: VisualizerConfig): number | void;
  
  /**
   * Sample a waveform at a given angle
   */
  protected sampleWaveform(waveform: string | undefined, angle: number): number {
    switch (waveform) {
      case 'square': {
        const v = Math.sin(angle);
        return v >= 0 ? 1 : -1;
      }
      case 'triangle':
        return (2 / Math.PI) * Math.asin(Math.sin(angle));
      case 'sawtooth': {
        const norm = ((angle / (2 * Math.PI)) % 1 + 1) % 1;
        return 2 * norm - 1;
      }
      default:
        return Math.sin(angle);
    }
  }
  
  /**
   * Helper to interpolate between Blue (#3b82f6) and Pink (#ec4899) based on pan
   */
  protected getPanColor(pan: number): string {
    const t = (pan + 1) / 2;
    const r = Math.floor(59 + (236 - 59) * t);
    const g = Math.floor(130 + (72 - 130) * t);
    const b = Math.floor(246 + (153 - 246) * t);
    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Get color based on wave's channel assignment using centralized helper
   * Left = Blue (#3b82f6), Right = Pink (#ec4899), Both/Default = Green (#10B981)
   */
  protected getChannelColor(channel?: string): string {
    return colorHelper.getChannelColor(channel);
  }

  /**
   * Get the appropriate color for a wave based on color mode
   * This is the main method to use in render() implementations
   */
  protected getWaveColor(wave: Wave, colorMode: 'wave' | 'channel' = 'wave'): string {
    return colorHelper.getWaveColor(wave, colorMode);
  }
  
  /**
   * Get color with alpha from wave
   */
  protected getWaveColorWithAlpha(wave: Wave, alpha: number = 0.6): string {
    const r = parseInt(wave.color.slice(1, 3), 16);
    const g = parseInt(wave.color.slice(3, 5), 16);
    const b = parseInt(wave.color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  
  /**
   * 3D Projection helper for 3D visualizers
   */
  protected project3D(
    x: number, 
    y: number, 
    z: number, 
    rotX: number, 
    rotY: number, 
    cameraZ: number,
    centerX: number,
    centerY: number
  ): { x: number; y: number } | null {
    // Rotate Y
    const rx = x * Math.cos(rotY) - z * Math.sin(rotY);
    let rz = x * Math.sin(rotY) + z * Math.cos(rotY);
    
    // Rotate X
    const ry = y * Math.cos(rotX) - rz * Math.sin(rotX);
    rz = y * Math.sin(rotX) + rz * Math.cos(rotX);

    const depth = rz + cameraZ;
    if (depth < 10) return null; // Clip
    
    const screenX = centerX + (rx / depth) * 1000;
    const screenY = centerY + (ry / depth) * 1000;
    
    return { x: screenX, y: screenY };
  }
  
  /**
   * Check if a wave is active (not muted and has amplitude)
   */
  protected isActive(wave: Wave): boolean {
    return !wave.muted && wave.amp > 0;
  }
  
  /**
   * Draw background grid
   */
  protected drawGrid(ctx: CanvasRenderingContext2D, width: number, height: number, style: 'simple' | 'detailed' = 'simple'): void {
    ctx.strokeStyle = 'rgba(99, 102, 241, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    
    if (style === 'detailed') {
      const gridStep = width / 10;
      for (let i = 0; i < width; i += gridStep) {
        ctx.moveTo(i, 0);
        ctx.lineTo(i, height);
      }
      const verticalGridStep = height / 5;
      for (let i = 0; i < height; i += verticalGridStep) {
        ctx.moveTo(0, i);
        ctx.lineTo(width, i);
      }
    } else {
      // Simple crosshair
      const cx = width / 2;
      const cy = height / 2;
      ctx.moveTo(cx, 0);
      ctx.lineTo(cx, height);
      ctx.moveTo(0, cy);
      ctx.lineTo(width, cy);
    }
    
    ctx.stroke();
  }
}
